import { useEffect, useRef, useState } from 'react';
import { Archive, DotsThree } from '@phosphor-icons/react';
import { dmsApi, fmtDate } from './dmsApi';
import { ConfBadge, fileIcon, hasThumb } from './docsUi';

const DRAWING_TYPES = ['drawing', 'assembly', 'part_drawing', 'blueprint'];

/**
 * 書類カード v3（UX-2.5A）。
 * 図面は「図番」を主タイトルにし、製品名や部品名は補足に回します。
 * 主タイトルは省略されても title 属性で全文を読めるようにします。
 * メタ情報は最大2行。それ以上は +N にまとめ、カードが縦に伸びるのを防ぎます。
 */
function identify(d) {
  const isDrawing = DRAWING_TYPES.includes(d.doc_type) || DRAWING_TYPES.includes(d.document_type);
  const no = d.drawing_number || d.document_number;
  if (isDrawing && no) {
    return { primary: no, secondary: d.title, mono: true };
  }
  return { primary: d.title, secondary: d.document_number, mono: false };
}

export default function DocCard({ doc: d, onOpen, selectable, selected, onSelect, small,
                                 size = 'md', actions }) {
  const [thumb, setThumb] = useState(null);
  const rootRef = useRef(null);
  // D7 — カードごとの ⋯。削除がビューアーの奥にしか無く、確認も無かった。
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDoc = (e) => { if (!menuRef.current?.contains(e.target)) setMenuOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [menuOpen]);

  useEffect(() => {
    let url = null;
    let alive = true;
    const el = rootRef.current;
    if (!hasThumb(d) || !el) return undefined;
    // L5 — 画面に入ったものだけ取りに行く。以前はカードの数だけ（300件なら
    // 300本）同時にサムネイルを要求していて、一覧が出るまで待たされた。
    const fetchThumb = () => {
      dmsApi.thumbUrl(d.id).then((u) => {
        if (!u) return;                       // 204 = サムネイル無し
        if (alive) { url = u; setThumb(u); } else { URL.revokeObjectURL(u); }
      }).catch(() => {});
    };
    if (typeof IntersectionObserver === 'undefined') {
      fetchThumb();
      return () => { alive = false; if (url) URL.revokeObjectURL(url); };
    }
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { io.disconnect(); fetchThumb(); }
    }, { rootMargin: '300px' });
    io.observe(el);
    return () => { alive = false; io.disconnect(); if (url) URL.revokeObjectURL(url); };
  }, [d]);

  const protected_ = d.preview_allowed === false;
  const { primary, secondary, mono } = identify(d);
  const label = protected_ ? '（保護された書類）' : (primary || '無題の書類');
  const chips = d.links || [];
  const isDrawing = DRAWING_TYPES.includes(d.doc_type) || DRAWING_TYPES.includes(d.document_type);
  const thumbH = small ? 'h-24' : { sm: 'h-24', md: 'h-32', lg: 'h-52' }[size];
  const filename = d.current_filename || d.original_filename;

  return (
    <div ref={rootRef} className="relative group min-w-0" data-testid={`dms-doc-${d.id}`}>
      {selectable && (
        // L2 — 以前はチェックボックスが「図面」バッジの上に重なり、バッジが
        // 隠れて読めなかった。白地の枠に入れて左上を占有し、バッジは右へずらす。
        <label className="absolute top-1.5 left-1.5 z-20 inline-flex items-center justify-center
          h-5 w-5 rounded bg-white/95 shadow-sm ring-1 ring-zinc-200 cursor-pointer">
          <input type="checkbox" checked={!!selected} onChange={(e) => onSelect?.(e.target.checked)}
            onClick={(e) => e.stopPropagation()}
            aria-label={`${label} を選択`}
            className="h-3.5 w-3.5 accent-teal-600"
            data-testid={`dms-select-${d.id}`} />
        </label>
      )}
      {(actions || []).length > 0 && (
        <div className="absolute top-1.5 right-1.5 z-20" ref={menuRef}>
          <button type="button" aria-label={`${label} のその他の操作`} aria-haspopup="menu"
            aria-expanded={menuOpen} data-testid={`dms-doc-menu-${d.id}`}
            onClick={(e) => { e.stopPropagation(); setMenuOpen((o) => !o); }}
            className="rounded-md bg-white/90 p-1 text-zinc-500 shadow-sm ring-1 ring-zinc-200 hover:text-zinc-800">
            <DotsThree size={16} weight="bold" />
          </button>
          {menuOpen && (
            <div role="menu" data-testid={`dms-doc-menu-list-${d.id}`}
              className="absolute right-0 mt-1 w-40 rounded-md border border-zinc-200 bg-white py-1 shadow-lg">
              {actions.map((a) => (
                <button key={a.label} type="button" role="menuitem"
                  data-testid={`dms-doc-menu-${a.key}-${d.id}`}
                  onClick={(e) => { e.stopPropagation(); setMenuOpen(false); a.onClick?.(d); }}
                  className={`block w-full px-3 py-1.5 text-left text-xs hover:bg-zinc-50 ${
                    a.tone === 'danger' ? 'text-rose-600' : 'text-zinc-700'}`}>
                  {a.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      <button type="button" onClick={() => onOpen?.(d.id)}
        data-testid={`dms-doc-card-${d.id}`}
        className="w-full h-full text-left rounded-lg border border-zinc-200 bg-white overflow-hidden flex flex-col hover:border-teal-300 hover:shadow-md focus-visible:ring-2 focus-visible:ring-teal-600 focus-visible:outline-none transition-[box-shadow,border-color] duration-200">
        <div className={`relative ${thumbH} bg-zinc-50 border-b border-zinc-200 flex items-center justify-center overflow-hidden`}>
          {thumb
            ? <img src={thumb} alt="" loading="lazy"
                style={Number((d.page_rotations || {})['1'] || 0)
                  ? { transform: `rotate(${Number(d.page_rotations['1'])}deg)` } : undefined}
                data-testid={`dms-doc-thumb-${d.id}`}
                className="h-full w-full object-cover object-top opacity-95 group-hover:opacity-100 transition-opacity duration-200" />
            : fileIcon(d, small || size === 'sm' ? 26 : 34)}
          {isDrawing && (
            <span className={`absolute top-1.5 rounded bg-sky-600 px-1.5 py-0.5 text-[10px] font-bold text-white ${
              selectable ? 'left-8' : 'left-1.5'}`}
              data-testid={`dms-doc-drawing-badge-${d.id}`}>図面</span>
          )}
          <span className={`absolute top-1.5 flex items-center gap-1 ${
            (actions || []).length > 0 ? 'right-9' : 'right-1.5'}`}>
            <ConfBadge doc={d} />
            {d.is_archived && <Archive size={13} weight="fill" className="text-stone-400"
              aria-label="アーカイブ済み" />}
          </span>
        </div>
        <div className="p-3 flex-1 flex flex-col gap-1">
          {/* 主識別子 — 読めることが最優先 */}
          <p title={label} data-no-translate data-testid={`dms-doc-primary-${d.id}`}
            className={`text-sm font-semibold text-zinc-900 truncate group-hover:text-teal-800 transition-colors duration-200 ${
              mono ? 'font-mono tracking-tight' : ''}`}>
            {label}
          </p>
          {/* 補足1行 */}
          {secondary && !protected_ && (
            <p title={secondary} data-no-translate className="text-xs text-zinc-600 truncate"
              data-testid={`dms-doc-secondary-${d.id}`}>{secondary}</p>
          )}
          {/* メタ1行 */}
          <p className="text-xs text-zinc-400 truncate"
            title={`${d.doc_type_label || ''} · ${fmtDate(d.updated_at)}`}>
            {d.doc_type_label} · {fmtDate(d.updated_at)}
          </p>
          {/* 元ファイル名 — スキャン取り込みでは唯一の手がかりになる */}
          {filename && !protected_ && size !== 'sm' && !small && (
            <p className="text-xs text-zinc-400 truncate" title={filename}
              data-testid={`dms-doc-filename-${d.id}`}>元ファイル名: <span data-no-translate>{filename}</span></p>
          )}
          {/* 追加情報は +N にまとめる（カードが縦に伸びない） */}
          {(chips.length > 0 || (d.match_reasons || []).length > 0) && (
            <span className="flex items-center gap-1 min-w-0" data-testid={`dms-chips-${d.id}`}>
              {(d.match_reasons || []).slice(0, 1).map((r) => (
                <span key={r} title={r}
                  className="shrink-0 rounded border border-teal-100 bg-teal-50 px-1.5 py-0.5 text-xs text-teal-700 max-w-[120px] truncate"
                  data-testid={`dms-match-${d.id}`}>{r}</span>
              ))}
              {chips.slice(0, 1).map((c) => (
                <span key={`${c.type}-${c.id}`} title={`${c.type_label}: ${c.label}`}
                  className="min-w-0 inline-flex items-center gap-1 rounded-full border border-zinc-200 bg-white px-2 py-0.5 text-xs text-zinc-600">
                  <span className="text-zinc-400 shrink-0">{c.type_label}</span>
                  <span className="truncate">{c.label}</span>
                </span>
              ))}
              {(chips.length + (d.match_reasons || []).length) > 2 && (
                <span title={[...(d.match_reasons || []), ...chips.map((c) => c.label)].join(' / ')}
                  className="shrink-0 inline-flex items-center rounded-full border border-zinc-200 bg-zinc-50 px-1.5 py-0.5 text-xs text-zinc-500"
                  data-testid={`dms-chip-more-${d.id}`}>
                  +{chips.length + (d.match_reasons || []).length - 2}
                </span>
              )}
            </span>
          )}
        </div>
      </button>
    </div>
  );
}
